/**
 * Route context for multi-project / multi-module API headers.
 *
 * Supported app routes (HashRouter):
 *   /:project/:module                          → project, module (no vertical)
 *   /:category/:project/:module                → project, module (legacy; vertical not sent)
 *   /:project/:module/maps/:mapType | /rtc     → same project/module (sub-routes ignored)
 *
 * Non-context routes (admin, auth, panels) yield empty context — no project/module headers.
 */

const LEGACY_VERTICAL_PREFIX = "gas-distribution"

/** Routes that are not project/module scoped */
const NON_CONTEXT_ROOTS = new Set([
  "admin",
  "admin-panel",
  "user-access-panel",
  "project-access-panel",
  "all-project",
  "login",
  "verify-otp",
  "forgot-password",
  "error",
  "no",
  "misc",
  "notifications"
])

/**
 * Resolve the app path from HashRouter (hash) or BrowserRouter (pathname).
 * @param {string} [href]
 * @returns {string} pathname like "/thinkgas/hes"
 */
export const getAppPathname = (href) => {
  if (typeof window === "undefined" && !href) return ""

  const source = href || window.location.href
  const hashIndex = source.indexOf("#")

  if (hashIndex !== -1) {
    const afterHash = source.slice(hashIndex + 1)
    const path = afterHash.split(/[?#]/)[0] || ""
    if (!path) return "/"
    return path.startsWith("/") ? path : `/${path}`
  }

  try {
    return new URL(source).pathname || "/"
  } catch {
    return typeof window !== "undefined" ? window.location.pathname || "/" : "/"
  }
}

/**
 * Percent-decode one path segment, tolerating malformed input.
 *
 * A pathname reaches us encoded — a project named "Production Toolkit" arrives
 * as "production%20toolkit". Left encoded it matches no token entry, so the
 * route resolves to the not-found page. `decodeURIComponent` throws on a lone
 * "%", hence the guard.
 */
const decodeSegment = (value) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/** Split a pathname into decoded, non-empty segments. */
const toSegments = (path) =>
  String(path)
    .split("/")
    .map((p) => decodeSegment(p).trim())
    .filter(Boolean)

/**
 * Parse project/module (and optional legacy vertical) from a pathname.
 * @param {string} [pathname]
 * @returns {{ vertical: string|null, project: string|null, module: string|null, hasContext: boolean }}
 */
export const getRouteContext = (pathname) => {
  const path = pathname ?? getAppPathname()
  const parts = toSegments(path)

  const empty = { vertical: null, project: null, module: null, hasContext: false }

  if (parts.length === 0) return empty

  const root = parts[0].toLowerCase()
  if (NON_CONTEXT_ROOTS.has(root)) return empty

  // Legacy: /gas-distribution/:project/:module[/...]
  if (root === LEGACY_VERTICAL_PREFIX) {
    const project = parts[1] || null
    const module = parts[2] || null
    if (!project || !module) return empty
    return {
      vertical: parts[0],
      project,
      module,
      hasContext: true
    }
  }

  // Modern: /:project/:module[/...]
  const project = parts[0] || null
  const module = parts[1] || null
  if (!project || !module) return empty

  return {
    vertical: null,
    project,
    module,
    hasContext: true
  }
}

/**
 * Route context plus the module's own base path and trailing sub-route.
 *
 * Screens that link to their own sub-pages (HES tabs, meter detail) must not
 * rebuild the path from raw segment indexes — the index of the module differs
 * between the modern and legacy schemes, and getting it wrong duplicates the
 * module segment (`/thinkgas/hes/hes/meter-profile`). Compose from `basePath`
 * instead: `${basePath}/meter-profile`.
 *
 * @param {string} [pathname]
 * @returns {{ vertical: string|null, project: string|null, module: string|null,
 *             hasContext: boolean, basePath: string, segments: string[] }}
 */
export const getModuleRoute = (pathname) => {
  const path = pathname ?? getAppPathname()
  const context = getRouteContext(path)

  if (!context.hasContext) return { ...context, basePath: "", segments: [] }

  const parts = toSegments(path)

  // Legacy paths carry the vertical ahead of the project, so the module sits
  // one segment further along.
  const moduleIndex = context.vertical ? 2 : 1

  return {
    ...context,
    basePath: `/${parts.slice(0, moduleIndex + 1).join("/")}`,
    segments: parts.slice(moduleIndex + 1)
  }
}

/**
 * Path to a module of the *current* project, for cross-module links (e.g. a
 * Dashboard list linking into HES → meter profile). Keeps the legacy vertical
 * prefix when the current route has one.
 *
 *   buildModulePath("/thinkgas/dashboard", "hes", "meter-profile", "12345")
 *     -> "/thinkgas/hes/meter-profile/12345"
 *
 * @param {string} pathname current location
 * @param {string} module   target module segment
 * @param {...string} rest  extra segments, appended as-is (encode first)
 * @returns {string} path, or "" when the current route has no project context
 */
export const buildModulePath = (pathname, module, ...rest) => {
  const { vertical, project } = getRouteContext(pathname)
  if (!project || !module) return ""
  const prefix = vertical ? `/${vertical}` : ""
  return [`${prefix}/${project}/${module}`, ...rest.filter(Boolean)].join("/")
}

/**
 * Apply route context headers onto axios / Fetch Headers objects.
 * - Always omits `vertical` (APIs expect project + module only)
 * - Clears stale vertical/project/module when outside project context
 *
 * @param {import('axios').AxiosRequestHeaders | Headers | Record<string, any>} headers
 * @param {{ project?: string|null, module?: string|null, hasContext?: boolean }} [context]
 */
export const applyRouteContextHeaders = (headers, context = getRouteContext()) => {
  if (!headers) return headers

  const removeHeader = (key) => {
    if (typeof headers.delete === "function") {
      headers.delete(key)
    } else {
      delete headers[key]
    }
  }

  const setHeader = (key, value) => {
    if (typeof headers.set === "function") {
      headers.set(key, value)
    } else {
      headers[key] = value
    }
  }

  removeHeader("vertical")

  if (context?.hasContext && context.project && context.module) {
    setHeader("project", context.project)
    setHeader("module", context.module)
  } else {
    removeHeader("project")
    removeHeader("module")
  }

  return headers
}
